import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Phone, HeartHandshake } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { EASE, MaskedLine, Reveal } from "@/components/motion";
import { FreeBookClaim } from "@/components/FreeBookClaim";
import { ContactButton } from "@/components/ContactDialog";

const Header = () => (
  <motion.header
    className="sticky top-0 z-50 w-full bg-black border-b-2 border-white/20 text-white"
    data-testid="gambling-header"
    initial={{ y: -80 }}
    animate={{ y: 0 }}
    transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
  >
    <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-12 py-4">
      <Link to="/" className="flex items-center gap-3" data-testid="gambling-logo-link">
        <LogoMark size={30} dark />
        <span className="font-display text-base sm:text-2xl uppercase tracking-tight whitespace-nowrap">
          The Last <span className="text-[#E60000]">Bet</span> Project
        </span>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm font-semibold uppercase tracking-widest">
        <Link to="/poker" data-testid="gambling-nav-poker" className="hover:text-[#E60000] transition-colors">Poker Project</Link>
        <a href="#signs" data-testid="gambling-nav-signs" className="hover:text-[#E60000] transition-colors">The Signs</a>
        <a href="#help" data-testid="gambling-nav-help" className="hover:text-[#E60000] transition-colors">Get Help</a>
      </nav>
      <div className="flex items-center gap-3">
        <ContactButton dark iconOnly />
        <Link
          to="/"
          data-testid="gambling-back-link"
          className="inline-flex items-center gap-2 border-2 border-white bg-transparent px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-[#E60000] hover:border-[#E60000] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Main Site
        </Link>
      </div>
    </div>
  </motion.header>
);

const HERO_LINES = [
  { text: "The odds were", accent: false },
  { text: "never in", accent: false },
  { text: "your favor.", accent: true },
];

const Hero = () => (
  <section className="bg-black text-white border-b-2 border-black" data-testid="gambling-hero">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-28">
      <motion.div
        className="inline-flex items-center gap-3 border-2 border-white px-4 py-2 mb-10"
        data-testid="gambling-eyebrow"
        initial={{ opacity: 0, x: -32 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
      >
        <span className="w-2.5 h-2.5 bg-[#E60000] inline-block flex-shrink-0" />
        <span className="text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.12em] font-bold">
          The Anti Gambling Addiction Project
        </span>
      </motion.div>
      <h1
        className="font-display uppercase leading-[0.95] tracking-wide text-5xl sm:text-7xl lg:text-[7.5vw]"
        data-testid="gambling-hero-title"
      >
        {HERO_LINES.map((line, i) => (
          <MaskedLine key={line.text} delay={0.6 + i * 0.14}>
            {line.accent ? <span className="text-[#E60000]">{line.text}</span> : line.text}
          </MaskedLine>
        ))}
      </h1>
      <div className="overflow-hidden mt-8">
        <motion.p
          className="font-script leading-[1] text-4xl sm:text-5xl lg:text-6xl text-[#E60000]"
          data-testid="gambling-hero-script"
          initial={{ y: "110%", rotate: 2 }}
          animate={{ y: "0%", rotate: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
        >
          Your life was never supposed to be one of them.
        </motion.p>
      </div>
      <motion.p
        className="mt-8 max-w-2xl text-lg lg:text-xl text-neutral-300 font-medium leading-relaxed"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
      >
        Slots. Sports betting. Blackjack. Roulette. The lottery ticket at the gas
        station. Different games, same machine — a machine engineered by
        billion-dollar industries to keep you pulling, tapping, and chasing. This
        page exists for one reason: to help you see the machine.
      </motion.p>
      <motion.div
        className="mt-10 flex flex-col sm:flex-row gap-4"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.55, ease: EASE }}
      >
        <motion.a
          href="#signs"
          data-testid="gambling-cta-signs"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          className="inline-flex items-center justify-center gap-2 bg-[#E60000] text-white border-2 border-white px-8 py-4 font-bold uppercase tracking-widest text-sm"
        >
          Learn The Warning Signs <ArrowRight className="w-4 h-4" />
        </motion.a>
        <motion.a
          href="#help"
          data-testid="gambling-cta-help"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.96 }}
          className="inline-flex items-center justify-center bg-transparent text-white border-2 border-white px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-white hover:text-black transition-colors"
        >
          I Need Help Now
        </motion.a>
      </motion.div>
    </div>
  </section>
);

const MarqueeBand = () => (
  <section className="bg-[#E60000] text-white border-b-2 border-black" data-testid="gambling-marquee">
    <Marquee speed={30} gradient={false} className="py-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <span key={i} className="font-display text-5xl lg:text-7xl uppercase mx-8 flex items-center gap-8">
          You Are Not Alone.
          <span className="text-outline-white">It's Not Your Fault.</span>
          Help Exists.
          <span className="w-3 h-3 bg-white inline-block" />
        </span>
      ))}
    </Marquee>
  </section>
);

const SIGNS = [
  { title: "Chasing losses", text: "You lost money and your first thought is how to win it back. That thought is the trap working exactly as designed." },
  { title: "Hiding it", text: "You downplay how much you gamble, delete apps before someone sees your phone, or lie about where the money went." },
  { title: "Needing bigger hits", text: "The bets that used to excite you feel like nothing now. You raise the stakes just to feel the same rush." },
  { title: "Gambling to escape", text: "Stress, sadness, boredom, anger — gambling became the off-switch for feelings you don't want to feel." },
  { title: "Money that isn't yours", text: "Borrowing, credit cards, savings meant for bills — the money line keeps moving further than you ever planned." },
  { title: "Broken promises", text: "You've told yourself — or someone you love — 'this is the last time' more times than you can count." },
  { title: "It's always on your mind", text: "Replaying hands, checking odds, planning the next session while you're at work, at dinner, at your kid's game." },
  { title: "You can't just stop", text: "You've tried. Maybe for days, maybe weeks. And something always pulls you back. That pull has a name: addiction." },
];

const Signs = () => (
  <section id="signs" className="bg-white border-b-2 border-black" data-testid="gambling-signs">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <Reveal>
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Step 01 — See it clearly</div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9] max-w-4xl">
          The warning <span className="text-[#E60000]">signs</span> nobody taught you.
        </h2>
        <p className="mt-6 max-w-2xl text-lg text-neutral-700 font-medium">
          If two or three of these sound like your life, this is not a coincidence
          and it is not a character flaw. Read them slowly. Be honest — nobody is
          watching.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-2 border-black" data-testid="gambling-signs-grid">
        {SIGNS.map((s, i) => (
          <Reveal key={s.title} delay={(i % 4) * 0.08} className={`border-black ${i % 4 !== 3 ? "lg:border-r-2" : ""} ${i % 2 === 0 ? "sm:border-r-2 lg:border-r-2" : ""} border-b-2 lg:border-b-2 ${i >= 4 ? "lg:border-b-0" : ""} ${i >= 6 ? "sm:border-b-0 lg:border-b-0" : ""}`}>
            <div className="p-6 lg:p-8 h-full" data-testid={`gambling-sign-${i}`}>
              <div className="font-display text-4xl text-[#E60000]">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-2xl uppercase mt-3 leading-tight">{s.title}</h3>
              <p className="mt-3 text-sm text-neutral-700 font-medium leading-relaxed">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Trap = () => (
  <section className="bg-[#F5F2EC] border-b-2 border-black" data-testid="gambling-trap">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Step 02 — Understand the machine</div>
          <h2 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9]">
            It's not weakness.<br />It's <span className="text-[#E60000]">engineering.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 space-y-5 text-base lg:text-lg font-medium text-neutral-800 max-w-2xl leading-relaxed">
            <p>
              I spent nearly thirty years inside gambling addiction. Poker was my
              poison, but the machine underneath is identical in every casino, every
              betting app, every scratch-off ticket:{" "}
              <span className="font-bold text-black">
                unpredictable rewards, delivered at unpredictable times.
              </span>
            </p>
            <p>
              That pattern is the most powerful behavior hook known to psychology.
              Your brain releases dopamine not when you win — but in the moment
              <span className="italic"> before</span> you find out. The near-miss.
              The almost. The "one more." None of it is accidental. Rooms, lights,
              sounds, apps, odds — all of it is engineered by people who profit
              when you can't stop.
            </p>
            <p>
              So hear me on this, because it took me three decades to learn it:{" "}
              <span className="font-bold text-[#E60000] uppercase tracking-wide">
                it's not your fault.
              </span>{" "}
              You're not weak, not stupid, not broken. You are a person with a
              normal brain up against an industry built to hack it. You are sick —
              and sick people need treatment, not shame.
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-5 flex items-center">
        <Reveal delay={0.2} className="w-full">
          <div className="border-2 border-black bg-black text-white p-8 lg:p-10 shadow-brutal-red" data-testid="gambling-trap-quote">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000] mb-4">From someone who lived it</div>
            <p className="font-display text-2xl lg:text-3xl uppercase leading-tight">
              "I wasn't choosing to destroy my life. I was being pulled by
              something I couldn't control. Admitting I was sick was the first
              move that actually worked."
            </p>
            <div className="mt-6 text-xs uppercase tracking-widest font-bold text-neutral-400">
              — The author, 30 years in the cycle, now free
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const STEPS = [
  { n: "01", title: "Say it out loud", text: "'I have a gambling problem.' To yourself, in a mirror, today. Naming the sickness is the moment it starts losing power over you." },
  { n: "02", title: "Tell one person", text: "One human being who loves you. Secrecy is the addiction's oxygen — honesty suffocates it. You will be shocked how many people respond with love, not judgment." },
  { n: "03", title: "Block the access", text: "Self-exclusion programs at casinos, blocking software on your phone, accounts closed, cards handed to someone you trust. Willpower alone loses to engineering — remove the door." },
  { n: "04", title: "Call the helpline", text: "In the US: 1-800-GAMBLER — free, confidential, 24/7. Outside the US, search for your national gambling helpline. One call. That's it. They have heard everything before." },
  { n: "05", title: "Find your people", text: "Gamblers Anonymous meetings (in person and online) put you in a room with people who get it — no explaining, no shame. Recovery is a team sport." },
];

const Help = () => (
  <section id="help" className="bg-black text-white border-b-2 border-black" data-testid="gambling-help">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <Reveal>
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">Step 03 — The way out</div>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
          Five moves.<br /><span className="text-[#E60000]">Starting tonight.</span>
        </h2>
      </Reveal>
      <div className="mt-14 border-2 border-white">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.05}>
            <div
              className={`grid grid-cols-12 gap-4 p-6 lg:p-8 items-start ${i > 0 ? "border-t-2 border-white" : ""}`}
              data-testid={`gambling-step-${i}`}
            >
              <div className="col-span-3 sm:col-span-2 font-display text-4xl lg:text-5xl text-[#E60000]">{s.n}</div>
              <div className="col-span-9 sm:col-span-10">
                <h3 className="font-display text-2xl lg:text-3xl uppercase">{s.title}</h3>
                <p className="mt-2 text-neutral-300 font-medium leading-relaxed max-w-3xl">{s.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-10 border-2 border-white p-6 lg:p-8 flex flex-col md:flex-row items-start md:items-center gap-6" data-testid="gambling-helpline-card">
          <div className="border-2 border-[#E60000] p-4 flex-shrink-0">
            <Phone className="w-8 h-8 text-[#E60000]" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">In crisis or on the edge right now?</div>
            <div className="font-display text-3xl lg:text-4xl mt-1">1-800-GAMBLER <span className="text-lg text-neutral-400">(1-800-426-2537)</span></div>
            <p className="mt-2 text-sm text-neutral-400 font-medium">Free. Confidential. 24/7. United States. If you're elsewhere, your country has one too — search "gambling helpline" + your country.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const CrossLinks = () => (
  <section className="bg-white border-b-2 border-black" data-testid="gambling-crosslinks">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
      <Reveal>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase leading-[0.9]">
          It ends <span className="text-[#E60000]">today.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-lg text-neutral-700 font-medium">
          This page is one initiative of The Last Bet Project — a small,
          independent recovery project funded by readers and donors, not casinos.
        </p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border-2 border-black">
        <Reveal delay={0.05} className="border-b-2 md:border-b-0 md:border-r-2 border-black">
          <Link to="/poker" className="block p-8 lg:p-10 h-full hover:bg-black hover:text-white transition-colors group" data-testid="gambling-link-poker">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">Anti Poker Addiction Project</div>
            <div className="font-display text-3xl uppercase mt-3 leading-tight">Our main mission — and the book</div>
            <p className="mt-3 text-sm font-medium text-neutral-600 group-hover:text-neutral-300">The recovery field manual written from 30 years inside the cycle. Free, instant download.</p>
            <div className="mt-6 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs">Visit <ArrowRight className="w-4 h-4" /></div>
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="border-b-2 md:border-b-0 md:border-r-2 border-black">
          <Link to="/#author" className="block p-8 lg:p-10 h-full hover:bg-black hover:text-white transition-colors group" data-testid="gambling-link-story">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">The author's story</div>
            <div className="font-display text-3xl uppercase mt-3 leading-tight">I lost it all so you don't have to</div>
            <p className="mt-3 text-sm font-medium text-neutral-600 group-hover:text-neutral-300">Homelessness, relapses, rock bottom — and the secret that finally broke the cycle.</p>
            <div className="mt-6 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs">Read it <ArrowRight className="w-4 h-4" /></div>
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/#donate" className="block p-8 lg:p-10 h-full bg-black text-white hover:bg-[#E60000] transition-colors" data-testid="gambling-link-donate">
            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#E60000]">Fuel the mission</div>
            <div className="font-display text-3xl uppercase mt-3 leading-tight">Keep these pages free for everyone</div>
            <p className="mt-3 text-sm font-medium text-neutral-300">One-time or monthly — every dollar keeps awareness in front of people who need it.</p>
            <div className="mt-6 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs">Donate <HeartHandshake className="w-4 h-4" /></div>
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-[#0A0A0A] text-white" data-testid="gambling-footer">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <LogoMark size={26} dark />
          <span className="font-display text-lg uppercase">The Last Bet Project</span>
          <span className="text-xs uppercase tracking-widest font-bold text-neutral-500">/ Anti Gambling Addiction Project</span>
        </div>
        <div className="flex items-center gap-6 text-xs uppercase tracking-widest font-bold text-neutral-500">
          <Link to="/privacy" data-testid="gambling-footer-privacy" className="hover:text-[#E60000] transition-colors">Privacy</Link>
          <Link to="/terms" data-testid="gambling-footer-terms" className="hover:text-[#E60000] transition-colors">Terms</Link>
          <Link to="/" data-testid="gambling-footer-home" className="hover:text-[#E60000] transition-colors">Main Site</Link>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t-2 border-white/20 text-center text-xs text-neutral-500 uppercase tracking-widest font-bold">
        If you're in crisis, call 1-800-GAMBLER (US) or your local helpline.
      </div>
    </div>
  </footer>
);

export default function Gambling() {
  return (
    <div className="min-h-screen bg-white font-body text-black" data-testid="gambling-page">
      <Header />
      <Hero />
      <MarqueeBand />
      <Signs />
      <Trap />
      <Help />
      <FreeBookClaim
        packageId="ebook_gambling"
        title="The Secret to Stop Gambling Addiction"
        cover="/cover-gambling.jpg"
        tagline="The same raw truth, aimed at the whole machine — slots, sports betting, blackjack, roulette, and every bet in between."
      />
      <CrossLinks />
      <Footer />
    </div>
  );
}
